import React, { useState, useEffect, useRef, useMemo } from 'react';
import { pbAuthedFetch } from "../../authHeaders";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon, TitleIcon } from './icons';
import { Meter, Legend, SummaryBand } from './viz';

// Connector configuration cards. Card order: Keycloak, S3, Jira, Confluence.
// Loads config from GET /api/connectors (routes/connectors.js — the generic
// :name router, KNOWN_CONNECTORS = keycloak/s3/jira/outlook/teams/confluence),
// saves with PUT, tests with POST. Not /api/settings/connectors — that's a
// different router (routes/settings.js) with one hardcoded route per key for
// a disjoint set of connectors (servicenow, intune, teams-webhook, ...), used
// by the standalone cards below instead.
//
// Microsoft (Outlook/Teams/Calendar) is NOT one of these cards — it isn't an
// admin-configured app-credential connector like the others. Atlas's Graph
// integration is per-user OAuth (see apps/api/src/microsoftGraph.js): each
// person connects their own account via a real consent redirect. That's
// MicrosoftConnectCard below, rendered separately from the CARDS loop.

interface CardRow {
  type: 'text' | 'password' | 'check';
  inputType?: string;
  label: string;
  id: string;
  ph?: string;
}

interface CardDef {
  key: string;
  title: string;
  icon: string;
  desc: string;
  rows: CardRow[];
}

const CARDS: CardDef[] = [
  {
    key: 'keycloak', title: 'Keycloak', icon: 'shield',
    desc: 'OpenID Connect identity provider. When enabled, the login page offers "Sign in with Keycloak" and the API verifies Keycloak tokens.',
    rows: [
      { type: 'check', id: 'stg_keycloak_enabled', label: ' Enabled' },
      { type: 'text', inputType: 'url', label: 'Auth server URL', id: 'stg_keycloak_authServerUrl', ph: 'https://keycloak.example.com' },
      { type: 'text', label: 'Realm', id: 'stg_keycloak_realm', ph: 'itpms' },
      { type: 'text', label: 'Client ID', id: 'stg_keycloak_clientId', ph: 'pmo-console' },
      { type: 'password', label: 'Client secret', id: 'stg_keycloak_clientSecret', ph: 'not set' },
    ],
  },
  {
    key: 's3', title: 'Amazon S3', icon: 'bucket',
    desc: 'Object storage for file attachments and generated artifacts (PDF/PNG exports).',
    rows: [
      { type: 'check', id: 'stg_s3_enabled', label: ' Enabled' },
      { type: 'text', label: 'Bucket', id: 'stg_s3_bucket', ph: 'artifacts' },
      { type: 'text', label: 'Region', id: 'stg_s3_region', ph: 'us-east-1' },
      { type: 'check', id: 'stg_s3_useTaskRole', label: ' Use the ECS task role for credentials' },
      { type: 'password', label: 'Access key ID', id: 'stg_s3_accessKeyId', ph: 'not set' },
      { type: 'password', label: 'Secret access key', id: 'stg_s3_secretAccessKey', ph: 'not set' },
    ],
  },
  {
    key: 'jira', title: 'Jira', icon: 'board',
    desc: 'Atlassian Jira Cloud. Powers the Jira board with real issues instead of demo data.',
    rows: [
      { type: 'check', id: 'stg_jira_enabled', label: ' Enabled' },
      { type: 'text', inputType: 'url', label: 'Base URL', id: 'stg_jira_baseUrl', ph: 'https://your-org.atlassian.net' },
      { type: 'text', inputType: 'email', label: 'Account email', id: 'stg_jira_email', ph: 'svc@agency.gov' },
      { type: 'text', label: 'Project key', id: 'stg_jira_projectKey', ph: 'ITPMS' },
      { type: 'password', label: 'API token', id: 'stg_jira_apiToken', ph: 'not set' },
    ],
  },
  {
    key: 'confluence', title: 'Confluence', icon: 'book',
    desc: 'Atlassian Confluence Cloud. Powers the Confluence page with real spaces and pages.',
    rows: [
      { type: 'check', id: 'stg_confluence_enabled', label: ' Enabled' },
      { type: 'text', inputType: 'url', label: 'Base URL', id: 'stg_confluence_baseUrl', ph: 'https://your-org.atlassian.net' },
      { type: 'text', inputType: 'email', label: 'Account email', id: 'stg_confluence_email', ph: 'svc@agency.gov' },
      { type: 'text', label: 'Space key (optional)', id: 'stg_confluence_spaceKey', ph: 'PMO' },
      { type: 'password', label: 'API token', id: 'stg_confluence_apiToken', ph: 'not set' },
    ],
  },
];

function fieldKey(cardKey: string, rowId: string): string {
  return rowId.slice(('stg_' + cardKey + '_').length);
}

interface ConnectorData {
  enabled?: boolean;
  config?: Record<string, any>;
  secretsSet?: string[];
}

interface JiraOverride {
  project: string;
  projectKey: string;
}

interface IntegrationsStatus {
  ok: boolean;
  microsoft: boolean;
}

// Real per-user Microsoft OAuth connect — powers Inbox, Teams, and Calendar's
// "My meetings" overlay, all through the same apps/api/src/microsoftGraph.js
// client. Distinct from the CARDS above: there's no form to save, just a
// consent redirect.
function MicrosoftConnectCard() {
  const [justConnected, setJustConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('microsoft') === 'connected') {
      setJustConnected(true);
      params.delete('microsoft');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  const { data: status } = useQuery<IntegrationsStatus>({
    queryKey: ['integrations-status'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/status');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  async function connect() {
    setConnecting(true);
    setConnectError('');
    try {
      const res = await pbAuthedFetch('/api/integrations/microsoft/auth');
      const body = await res.json();
      if (!res.ok || !body.authUrl) {
        setConnectError(body.error || `Request failed (${res.status})`);
        setConnecting(false);
        return;
      }
      window.location.href = body.authUrl;
    } catch {
      setConnectError('Could not reach the server.');
      setConnecting(false);
    }
  }

  return (
    <div className="box" data-card="microsoft">
      <header className="box__head">
        <TitleIcon name="mail" />
        <div className="box__head-text">
          <h2 className="box__title" id="stg_microsoft_h">Microsoft 365</h2>
        </div>
        <div className="box__head-actions">
          <span className={`pill${status?.microsoft ? ' pill--ok' : ''}`}><i />{status?.microsoft ? 'available' : 'not configured'}</span>
        </div>
      </header>
      <div className="box__body">
        <p className="stg-desc">
          Each person connects their own Microsoft account — this powers real mail and folders on the Inbox page, real channels and messages on the Teams page, and the "My meetings" overlay on Calendar. There's nothing to configure here beyond the app registration below; the connection itself happens per person.
        </p>
        {justConnected ? (
          <p className="stg-fig t-ok" style={{ fontWeight: 600 }}>Connected ✓ — your Microsoft account is linked.</p>
        ) : null}
        {connectError ? (
          <p className="stg-fig t-risk" style={{ fontWeight: 600 }}>{connectError}</p>
        ) : null}
        {status && !status.microsoft ? (
          <p className="stg-desc">Microsoft identity platform not configured for this deployment — an administrator needs to set the app registration's tenant ID, client ID, and client secret (server environment configuration, not stored here).</p>
        ) : null}
      </div>
      <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)' }}>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!status?.microsoft || connecting}
          onClick={connect}
        >
          {connecting ? 'Redirecting…' : 'Connect your Microsoft account'}
        </button>
      </div>
    </div>
  );
}

// Shared by every standalone card below (Teams, ServiceNow, Intune): they each
// own their own PUT/test routes (routes/settings.js has one hardcoded pair per
// connector key, no generic :name handler), but they all read from the same
// GET /api/settings/connectors list, so one query key means one network call
// no matter how many of these cards are mounted at once.
function useConnectorsMap() {
  return useQuery<Record<string, ConnectorData>>({
    queryKey: ['settings-connectors'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/settings/connectors');
      const json = await res.json();
      const list = Array.isArray(json.connectors) ? json.connectors : Object.values(json.connectors || json);
      return Object.fromEntries(list.map((c: any) => [c.key, c])) as Record<string, ConnectorData>;
    },
  });
}

// Populates the Incident Email card's "Sender user ID" dropdown — every
// Cognito user who has connected their Microsoft 365 account (id here is
// that user's cognito_sub, exactly what senderUserId needs to hold).
// Deliberately not gated on this query's isLoading: the card renders from
// useConnectorsMap independent of whether this list is slow or empty.
function useIncidentEmailSenders() {
  return useQuery<{ id: string; displayName: string; email: string }[]>({
    queryKey: ['incident-email-senders'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/settings/connectors/incident-email/senders');
      const json = await res.json();
      return Array.isArray(json.senders) ? json.senders : [];
    },
  });
}

// Shared save/dirty/result/enabled state for the standalone connector cards
// below (Teams, PagerDuty, Incident Email) — each owns its own field state
// and PUT body shape, but the enabled/dirty/result bookkeeping and the save
// mutation's PUT-and-invalidate wiring were identical copy-pasted boilerplate
// across all three.
function useStandaloneConnectorCard<T extends Record<string, any>>(
  connectorKey: string,
  buildBody: (enabled: boolean) => T,
  resetFieldsOnSave: () => void
) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [result, setResult] = useState<{ ok: boolean | null; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch(`/api/settings/connectors/${connectorKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody(enabled)),
      });
      return res.json();
    },
    onSuccess: () => {
      setResult({ ok: true, text: 'Saved ✓' });
      setDirty(false);
      resetFieldsOnSave();
      queryClient.invalidateQueries({ queryKey: ['settings-connectors'] });
    },
    onError: () => setResult({ ok: false, text: 'Save failed' }),
  });

  return { enabled, setEnabled, result, setResult, dirty, setDirty, saveMutation };
}

// Teams alert delivery — real-time critical-incident alerts (mim.js) and the
// scheduled outage digest (reports/scheduler.js) both post through this one
// configured webhook. Standalone card (own fetch calls), same reasoning as
// MicrosoftConnectCard above: the generic CARDS/saveMutation loop's PUT/POST
// routes don't exist for arbitrary connector names, only for the specific
// ones each has its own route pair for.
function TeamsWebhookCard() {
  const [webhookUrl, setWebhookUrl] = useState('');
  // GET /connectors returns webhookUrl masked to '••••••••' once it's set
  // (it's a standalone bearer credential, same as any other connector
  // secret). isSet just tracks whether a value exists server-side, so the
  // masked string itself never has to flow into editable state.
  const [isSet, setIsSet] = useState(false);

  const { data, isLoading } = useConnectorsMap();
  const { enabled, setEnabled, result, setResult, dirty, setDirty, saveMutation } = useStandaloneConnectorCard(
    'teams-webhook',
    (currentEnabled) => {
      // Only send webhookUrl when the user actually typed a new one; an
      // empty field means "keep the existing value", mirroring how the
      // CARDS-based password rows below treat a blank secret input.
      const body: { enabled: boolean; webhookUrl?: string } = { enabled: currentEnabled };
      if (webhookUrl) body.webhookUrl = webhookUrl;
      return body;
    },
    () => setWebhookUrl('')
  );

  useEffect(() => {
    const conn = data?.['teams-webhook'];
    if (!conn || dirty) return;
    setEnabled(!!conn.enabled);
    // Never populate the editable field with the fetched value — it's masked
    // to '••••••••' server-side, and a controlled input bound to that string
    // would silently re-save the mask as the real webhook URL the next time
    // the form is submitted (e.g. just toggling Enabled).
    setIsSet(!!conn.config?.webhookUrl);
    setWebhookUrl('');
  }, [data, dirty]);

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/settings/connectors/teams-webhook/test', { method: 'POST' });
      return res.json();
    },
    onSuccess: (res) => setResult({ ok: !!res.success, text: res.success ? 'Sample alert sent ✓' : `Failed: ${res.error || 'unknown'}` }),
    onError: () => setResult({ ok: false, text: 'Test failed' }),
  });

  if (isLoading) return null;

  return (
    <div className="box" data-card="teams-webhook">
      <header className="box__head">
        <TitleIcon name="shield" />
        <div className="box__head-text">
          <h2 className="box__title" id="stg_teams_webhook_h">Teams Alerts</h2>
        </div>
        <div className="box__head-actions">
          <span className={`pill${enabled ? ' pill--ok' : ''}`}><i />{enabled ? 'enabled' : 'disabled'}</span>
        </div>
      </header>
      <div className="box__body">
        <p className="stg-desc">
          Posts a real-time alert to a Teams channel when a major incident becomes (or stays) critical, and mirrors the scheduled Outage Digest report there too. Add an Incoming Webhook connector to your Teams channel and paste its URL below.
        </p>
        <div className="stg-row">
          <label className="stg-check">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }}
            /> Enabled
          </label>
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_teams_webhook_url">Webhook URL</label>
          <input
            className="stg-input"
            type="url"
            id="stg_teams_webhook_url"
            placeholder={isSet ? '•••••••• (set — leave blank to keep)' : 'https://outlook.office.com/webhook/...'}
            value={webhookUrl}
            onChange={(e) => { setWebhookUrl(e.target.value); setDirty(true); }}
          />
          {isSet ? <span className="pb-sr-only">Currently set.</span> : null}
        </div>
      </div>
      <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--primary btn--sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save</button>
        <button type="button" className="btn btn--sm" disabled={testMutation.isPending} onClick={() => testMutation.mutate()}>Send test alert</button>
        {dirty ? <span className="stg-note">Unsaved changes</span> : null}
        {result ? <span className={`stg-fig ${result.ok === true ? 't-ok' : result.ok === false ? 't-risk' : ''}`} style={{ marginLeft: 'auto', fontWeight: 600 }}>{result.text}</span> : null}
      </div>
    </div>
  );
}

// PagerDuty — Events API v2 trigger/resolve on critical incidents
// (routes/mim.js fireIncidentAlert/resolveIncidentChannels). Standalone
// card, same reasoning as TeamsWebhookCard: dedicated PUT/POST route pair.
function PagerDutyCard() {
  const [routingKey, setRoutingKey] = useState('');
  const [isSet, setIsSet] = useState(false);

  const { data, isLoading } = useConnectorsMap();
  const { enabled, setEnabled, result, setResult, dirty, setDirty, saveMutation } = useStandaloneConnectorCard(
    'pagerduty',
    (currentEnabled) => {
      const body: { enabled: boolean; routingKey?: string } = { enabled: currentEnabled };
      if (routingKey) body.routingKey = routingKey;
      return body;
    },
    () => setRoutingKey('')
  );

  useEffect(() => {
    const conn = data?.['pagerduty'];
    if (!conn || dirty) return;
    setEnabled(!!conn.enabled);
    setIsSet(!!conn.config?.routingKey);
    setRoutingKey('');
  }, [data, dirty]);

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/settings/connectors/pagerduty/test', { method: 'POST' });
      return res.json();
    },
    onSuccess: (res) => setResult({ ok: !!res.success, text: res.success ? 'Sample trigger sent ✓' : `Failed: ${res.error || 'unknown'}` }),
    onError: () => setResult({ ok: false, text: 'Test failed' }),
  });

  if (isLoading) return null;

  return (
    <div className="box" data-card="pagerduty">
      <header className="box__head">
        <TitleIcon name="shield" />
        <div className="box__head-text">
          <h2 className="box__title" id="stg_pagerduty_h">PagerDuty</h2>
        </div>
        <div className="box__head-actions">
          <span className={`pill${enabled ? ' pill--ok' : ''}`}><i />{enabled ? 'enabled' : 'disabled'}</span>
        </div>
      </header>
      <div className="box__body">
        <p className="stg-desc">
          Pages the on-call via PagerDuty's own escalation policy when a major incident becomes (or stays) critical, and resolves the page when the incident is marked resolved. Paste the Events API v2 integration routing key from your PagerDuty service below.
        </p>
        <div className="stg-row">
          <label className="stg-check">
            <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }} /> Enabled
          </label>
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_pagerduty_key">Routing key</label>
          <input
            className="stg-input"
            type="text"
            id="stg_pagerduty_key"
            placeholder={isSet ? '•••••••• (set — leave blank to keep)' : 'Events API v2 integration key'}
            value={routingKey}
            onChange={(e) => { setRoutingKey(e.target.value); setDirty(true); }}
          />
          {isSet ? <span className="pb-sr-only">Currently set.</span> : null}
        </div>
      </div>
      <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--primary btn--sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save</button>
        <button type="button" className="btn btn--sm" disabled={testMutation.isPending} onClick={() => testMutation.mutate()}>Send test trigger</button>
        {dirty ? <span className="stg-note">Unsaved changes</span> : null}
        {result ? <span className={`stg-fig ${result.ok === true ? 't-ok' : result.ok === false ? 't-risk' : ''}`} style={{ marginLeft: 'auto', fontWeight: 600 }}>{result.text}</span> : null}
      </div>
    </div>
  );
}

// Incident Email — sends as the configured senderUserId's connected
// Outlook account (email.js's notify(), which calls Graph's /me/sendMail —
// no app-only send identity exists, so a designated sender is required).
export function IncidentEmailCard() {
  const [recipients, setRecipients] = useState('');
  const [senderUserId, setSenderUserId] = useState('');

  const { data, isLoading } = useConnectorsMap();
  const { data: senders } = useIncidentEmailSenders();
  const { enabled, setEnabled, result, dirty, setDirty, saveMutation } = useStandaloneConnectorCard(
    'incident-email',
    (currentEnabled) => ({
      enabled: currentEnabled,
      recipients: recipients.split(',').map((s) => s.trim()).filter(Boolean),
      senderUserId,
    }),
    () => {}
  );

  useEffect(() => {
    const conn = data?.['incident-email'];
    if (!conn || dirty) return;
    setEnabled(!!conn.enabled);
    setRecipients((conn.config?.recipients || []).join(', '));
    setSenderUserId(conn.config?.senderUserId || '');
  }, [data, dirty]);

  if (isLoading) return null;

  return (
    <div className="box" data-card="incident-email">
      <header className="box__head">
        <TitleIcon name="mail" />
        <div className="box__head-text">
          <h2 className="box__title" id="stg_incident_email_h">Incident Email</h2>
        </div>
        <div className="box__head-actions">
          <span className={`pill${enabled ? ' pill--ok' : ''}`}><i />{enabled ? 'enabled' : 'disabled'}</span>
        </div>
      </header>
      <div className="box__body">
        <p className="stg-desc">
          Emails a distribution list, with the incident report PDF attached, when a major incident becomes (or stays) critical. Sends from the connected Microsoft 365 account of the sender you choose below — connect that account first under the Microsoft 365 connector.
        </p>
        <div className="stg-row">
          <label className="stg-check">
            <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }} /> Enabled
          </label>
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_incident_email_recipients">Recipients (comma-separated)</label>
          <input
            className="stg-input"
            type="text"
            id="stg_incident_email_recipients"
            placeholder="oncall-dl@example.com, sre-lead@example.com"
            value={recipients}
            onChange={(e) => { setRecipients(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_incident_email_sender">Sender user ID</label>
          <select
            className="stg-input"
            id="stg_incident_email_sender"
            value={senderUserId}
            onChange={(e) => { setSenderUserId(e.target.value); setDirty(true); }}
          >
            <option value="">— select sender —</option>
            {senderUserId && !(senders ?? []).some((s) => s.id === senderUserId) && (
              <option value={senderUserId}>{senderUserId} (not connected)</option>
            )}
            {(senders ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.displayName} ({s.email})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--primary btn--sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save</button>
        {dirty ? <span className="stg-note">Unsaved changes</span> : null}
        {result ? <span className={`stg-fig ${result.ok === true ? 't-ok' : result.ok === false ? 't-risk' : ''}`} style={{ marginLeft: 'auto', fontWeight: 600 }}>{result.text}</span> : null}
      </div>
    </div>
  );
}

// ServiceNow — powers the SN Ticket Analytics and ServiceNow Feed pages, and
// lets Major Incident Cmd file real incidents (routes/integrations.js
// POST /servicenow/incident). Standalone card, same reasoning as
// TeamsWebhookCard: routes/settings.js has a dedicated PUT/POST pair for
// this key with its own flat request-body shape, not the generic
// {enabled, config, secrets} shape the CARDS loop below sends.
function ServiceNowCard() {
  const queryClient = useQueryClient();
  const [instanceUrl, setInstanceUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordSet, setIsPasswordSet] = useState(false);
  const [syncInterval, setSyncInterval] = useState(30);
  const [enabled, setEnabled] = useState(false);
  const [result, setResult] = useState<{ ok: boolean | null; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useConnectorsMap();

  useEffect(() => {
    const conn = data?.['servicenow'];
    if (!conn || dirty) return;
    setEnabled(!!conn.enabled);
    setInstanceUrl(conn.config?.instance_url || '');
    setUsername(conn.config?.username || '');
    setSyncInterval(conn.config?.sync_interval_minutes || 30);
    setIsPasswordSet(!!conn.config?.password);
    setPassword('');
  }, [data, dirty]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        enabled, instance_url: instanceUrl, username, sync_interval_minutes: syncInterval,
      };
      if (password) body.password = password;
      const res = await pbAuthedFetch('/api/settings/connectors/servicenow', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.json();
    },
    onSuccess: () => {
      setResult({ ok: true, text: 'Saved ✓' });
      setDirty(false);
      setPassword('');
      queryClient.invalidateQueries({ queryKey: ['settings-connectors'] });
    },
    onError: () => setResult({ ok: false, text: 'Save failed' }),
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/settings/connectors/servicenow/test', { method: 'POST' });
      return res.json();
    },
    onSuccess: (res) => setResult({ ok: !!res.success, text: res.success ? (res.message || 'Connected ✓') : `Failed: ${res.error || 'unknown'}` }),
    onError: () => setResult({ ok: false, text: 'Test failed' }),
  });

  if (isLoading) return null;

  return (
    <div className="box" data-card="servicenow">
      <header className="box__head">
        <TitleIcon name="ticket" />
        <div className="box__head-text">
          <h2 className="box__title" id="stg_servicenow_h">ServiceNow</h2>
        </div>
        <div className="box__head-actions">
          <span className={`pill${enabled ? ' pill--ok' : ''}`}><i />{enabled ? 'enabled' : 'disabled'}</span>
        </div>
      </header>
      <div className="box__body">
        <p className="stg-desc">
          ITSM instance for tickets and incidents. Powers SN Ticket Analytics and the ServiceNow Feed with real data, and lets Major Incident Cmd file incidents directly.
        </p>
        <div className="stg-row">
          <label className="stg-check">
            <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }} /> Enabled
          </label>
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_sn_url">Instance URL</label>
          <input
            className="stg-input" type="url" id="stg_sn_url" placeholder="https://your-org.service-now.com"
            value={instanceUrl} onChange={(e) => { setInstanceUrl(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_sn_username">Username</label>
          <input
            className="stg-input" type="text" id="stg_sn_username" placeholder="svc-atlas"
            value={username} onChange={(e) => { setUsername(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_sn_password">Password</label>
          <input
            className="stg-input" type="password" id="stg_sn_password"
            placeholder={isPasswordSet ? '•••••••• (set — leave blank to keep)' : 'not set'}
            value={password} onChange={(e) => { setPassword(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_sn_sync">Sync interval (minutes)</label>
          <input
            className="stg-input" type="number" min={5} id="stg_sn_sync"
            value={syncInterval} onChange={(e) => { setSyncInterval(Number(e.target.value) || 30); setDirty(true); }}
          />
        </div>
      </div>
      <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--primary btn--sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save</button>
        <button type="button" className="btn btn--sm" disabled={testMutation.isPending} onClick={() => testMutation.mutate()}>Test connection</button>
        {dirty ? <span className="stg-note">Unsaved changes</span> : null}
        {result ? <span className={`stg-fig ${result.ok === true ? 't-ok' : result.ok === false ? 't-risk' : ''}`} style={{ marginLeft: 'auto', fontWeight: 600 }}>{result.text}</span> : null}
      </div>
    </div>
  );
}

// Microsoft Intune / SCCM — managed-device sync (routes/settings.js PUT/POST
// /connectors/intune, backed by lib/intune-client.js's Graph API client).
// Same standalone-card reasoning as ServiceNowCard above. Distinct from the
// per-user Microsoft 365 OAuth in MicrosoftConnectCard: this is an
// admin-configured app-credential connector (tenant/client id + secret),
// not a per-person consent flow.
function IntuneCard() {
  const queryClient = useQueryClient();
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isSecretSet, setIsSecretSet] = useState(false);
  const [syncInterval, setSyncInterval] = useState(60);
  const [managedDeviceSync, setManagedDeviceSync] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [result, setResult] = useState<{ ok: boolean | null; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useConnectorsMap();

  useEffect(() => {
    const conn = data?.['intune'];
    if (!conn || dirty) return;
    setEnabled(!!conn.enabled);
    setTenantId(conn.config?.tenant_id || '');
    setClientId(conn.config?.client_id || '');
    setSyncInterval(conn.config?.sync_interval_minutes || 60);
    setManagedDeviceSync(!!conn.config?.managed_device_sync);
    setIsSecretSet(!!conn.config?.client_secret);
    setClientSecret('');
  }, [data, dirty]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        enabled, tenant_id: tenantId, client_id: clientId,
        sync_interval_minutes: syncInterval, managed_device_sync: managedDeviceSync,
      };
      if (clientSecret) body.client_secret = clientSecret;
      const res = await pbAuthedFetch('/api/settings/connectors/intune', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.json();
    },
    onSuccess: () => {
      setResult({ ok: true, text: 'Saved ✓' });
      setDirty(false);
      setClientSecret('');
      queryClient.invalidateQueries({ queryKey: ['settings-connectors'] });
    },
    onError: () => setResult({ ok: false, text: 'Save failed' }),
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/settings/connectors/intune/test', { method: 'POST' });
      return res.json();
    },
    onSuccess: (res) => setResult({ ok: !!res.success, text: res.success ? 'Connected ✓' : `Failed: ${res.error || 'unknown'}` }),
    onError: () => setResult({ ok: false, text: 'Test failed' }),
  });

  if (isLoading) return null;

  return (
    <div className="box" data-card="intune">
      <header className="box__head">
        <TitleIcon name="devices" />
        <div className="box__head-text">
          <h2 className="box__title" id="stg_intune_h">Microsoft Intune / SCCM</h2>
        </div>
        <div className="box__head-actions">
          <span className={`pill${enabled ? ' pill--ok' : ''}`}><i />{enabled ? 'enabled' : 'disabled'}</span>
        </div>
      </header>
      <div className="box__body">
        <p className="stg-desc">
          Managed-device inventory via Microsoft Graph. App-registration credentials (tenant + client ID and secret), separate from the per-person Microsoft 365 connection above.
        </p>
        <div className="stg-row">
          <label className="stg-check">
            <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }} /> Enabled
          </label>
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_intune_tenant">Tenant ID</label>
          <input
            className="stg-input" type="text" id="stg_intune_tenant" placeholder="00000000-0000-0000-0000-000000000000"
            value={tenantId} onChange={(e) => { setTenantId(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_intune_client">Client ID</label>
          <input
            className="stg-input" type="text" id="stg_intune_client" placeholder="00000000-0000-0000-0000-000000000000"
            value={clientId} onChange={(e) => { setClientId(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_intune_secret">Client secret</label>
          <input
            className="stg-input" type="password" id="stg_intune_secret"
            placeholder={isSecretSet ? '•••••••• (set — leave blank to keep)' : 'not set'}
            value={clientSecret} onChange={(e) => { setClientSecret(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_intune_sync">Sync interval (minutes)</label>
          <input
            className="stg-input" type="number" min={5} id="stg_intune_sync"
            value={syncInterval} onChange={(e) => { setSyncInterval(Number(e.target.value) || 60); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-check">
            <input
              type="checkbox" checked={managedDeviceSync}
              onChange={(e) => { setManagedDeviceSync(e.target.checked); setDirty(true); }}
            /> Sync managed devices
          </label>
        </div>
      </div>
      <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--primary btn--sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save</button>
        <button type="button" className="btn btn--sm" disabled={testMutation.isPending} onClick={() => testMutation.mutate()}>Test connection</button>
        {dirty ? <span className="stg-note">Unsaved changes</span> : null}
        {result ? <span className={`stg-fig ${result.ok === true ? 't-ok' : result.ok === false ? 't-risk' : ''}`} style={{ marginLeft: 'auto', fontWeight: 600 }}>{result.text}</span> : null}
      </div>
    </div>
  );
}

// Active Directory — LDAP directory connector (routes/settings.js PUT/POST
// /connectors/active-directory, backed by the same lib/ldapjs bind+search
// logic as routes/ad-connector.js's waste-summary route, which is what
// already powers the AD Savings page). Backend has existed since before this
// card did — this is the first UI for it. Host/base DN/bind DN are LDAP
// directory-query settings, unrelated to the windows_exporter Prometheus
// scrape target configured separately for Domain Health monitoring.
//
// Note the key split: the DB row (and therefore useConnectorsMap()'s map)
// uses the underscored 'active_directory', while REGISTRY's nav key below
// uses the hyphenated 'active-directory' to match the URL path — same as
// every other standalone card, just spelled differently for this one.
function ActiveDirectoryCard() {
  const queryClient = useQueryClient();
  const [host, setHost] = useState('');
  const [port, setPort] = useState(389);
  const [baseDn, setBaseDn] = useState('');
  const [bindDn, setBindDn] = useState('');
  const [bindPassword, setBindPassword] = useState('');
  const [isSecretSet, setIsSecretSet] = useState(false);
  const [useLdaps, setUseLdaps] = useState(false);
  const [staleThresholdDays, setStaleThresholdDays] = useState(90);
  const [enabled, setEnabled] = useState(false);
  const [result, setResult] = useState<{ ok: boolean | null; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useConnectorsMap();

  useEffect(() => {
    const conn = data?.['active_directory'];
    if (!conn || dirty) return;
    setEnabled(!!conn.enabled);
    setHost(conn.config?.host || '');
    setPort(conn.config?.port || 389);
    setBaseDn(conn.config?.base_dn || '');
    setBindDn(conn.config?.bind_dn || '');
    setUseLdaps(!!conn.config?.use_ldaps);
    setStaleThresholdDays(conn.config?.stale_threshold_days || 90);
    setIsSecretSet(!!conn.config?.bind_password);
    setBindPassword('');
  }, [data, dirty]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        enabled, host, port, base_dn: baseDn, bind_dn: bindDn,
        use_ldaps: useLdaps, stale_threshold_days: staleThresholdDays,
      };
      if (bindPassword) body.bind_password = bindPassword;
      const res = await pbAuthedFetch('/api/settings/connectors/active-directory', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.json();
    },
    onSuccess: () => {
      setResult({ ok: true, text: 'Saved ✓' });
      setDirty(false);
      setBindPassword('');
      queryClient.invalidateQueries({ queryKey: ['settings-connectors'] });
    },
    onError: () => setResult({ ok: false, text: 'Save failed' }),
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await pbAuthedFetch('/api/settings/connectors/active-directory/test', { method: 'POST' });
      return res.json();
    },
    onSuccess: (res) => setResult({ ok: !!res.success, text: res.success ? 'Connected ✓' : `Failed: ${res.error || 'unknown'}` }),
    onError: () => setResult({ ok: false, text: 'Test failed' }),
  });

  if (isLoading) return null;

  return (
    <div className="box" data-card="active-directory">
      <header className="box__head">
        <TitleIcon name="layers" />
        <div className="box__head-text">
          <h2 className="box__title" id="stg_ad_h">Active Directory</h2>
        </div>
        <div className="box__head-actions">
          <span className={`pill${enabled ? ' pill--ok' : ''}`}><i />{enabled ? 'enabled' : 'disabled'}</span>
        </div>
      </header>
      <div className="box__body">
        <p className="stg-desc">
          LDAP directory connection for user, group, and OU queries — powers Asset Accountability and AD Savings. Separate from the Prometheus health-monitoring target configured on the Domain Health page.
        </p>
        <div className="stg-row">
          <label className="stg-check">
            <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }} /> Enabled
          </label>
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_ad_host">Host</label>
          <input
            className="stg-input" type="text" id="stg_ad_host" placeholder="dc01.corp.example.com"
            value={host} onChange={(e) => { setHost(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_ad_port">Port</label>
          <input
            className="stg-input" type="number" min={1} id="stg_ad_port"
            value={port} onChange={(e) => { setPort(Number(e.target.value) || 389); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_ad_basedn">Base DN</label>
          <input
            className="stg-input" type="text" id="stg_ad_basedn" placeholder="DC=corp,DC=example,DC=com"
            value={baseDn} onChange={(e) => { setBaseDn(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_ad_binddn">Bind DN</label>
          <input
            className="stg-input" type="text" id="stg_ad_binddn" placeholder="CN=svc-atlas,CN=Users,DC=corp,DC=example,DC=com"
            value={bindDn} onChange={(e) => { setBindDn(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_ad_password">Bind password</label>
          <input
            className="stg-input" type="password" id="stg_ad_password"
            placeholder={isSecretSet ? '•••••••• (set — leave blank to keep)' : 'not set'}
            value={bindPassword} onChange={(e) => { setBindPassword(e.target.value); setDirty(true); }}
          />
        </div>
        <div className="stg-row">
          <label className="stg-check">
            <input
              type="checkbox" checked={useLdaps}
              onChange={(e) => { setUseLdaps(e.target.checked); setDirty(true); }}
            /> Use LDAPS
          </label>
        </div>
        <div className="stg-row">
          <label className="stg-label" htmlFor="stg_ad_stale">Stale threshold (days)</label>
          <input
            className="stg-input" type="number" min={1} id="stg_ad_stale"
            value={staleThresholdDays} onChange={(e) => { setStaleThresholdDays(Number(e.target.value) || 90); setDirty(true); }}
          />
        </div>
      </div>
      <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--primary btn--sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save</button>
        <button type="button" className="btn btn--sm" disabled={testMutation.isPending} onClick={() => testMutation.mutate()}>Test connection</button>
        {dirty ? <span className="stg-note">Unsaved changes</span> : null}
        {result ? <span className={`stg-fig ${result.ok === true ? 't-ok' : result.ok === false ? 't-risk' : ''}`} style={{ marginLeft: 'auto', fontWeight: 600 }}>{result.text}</span> : null}
      </div>
    </div>
  );
}

// List+detail nav metadata — one entry per connector, whichever of the two
// backends (CARDS below vs. the standalone components above) actually owns
// it. Grouped by what an admin is looking for, not by implementation.
interface ConnectorMeta {
  key: string;
  title: string;
  icon: string;
  group: string;
  kind: 'card' | 'standalone';
}

const GROUPS = [
  { id: 'identity', label: 'Identity & Access' },
  { id: 'itsm', label: 'ITSM' },
  { id: 'microsoft', label: 'Microsoft' },
  { id: 'incident-response', label: 'Incident Response' },
  { id: 'storage-dev', label: 'Storage & Dev' },
];

const REGISTRY: ConnectorMeta[] = [
  { key: 'keycloak', title: 'Keycloak', icon: 'shield', group: 'identity', kind: 'card' },
  { key: 'active-directory', title: 'Active Directory', icon: 'layers', group: 'identity', kind: 'standalone' },
  { key: 'servicenow', title: 'ServiceNow', icon: 'ticket', group: 'itsm', kind: 'standalone' },
  { key: 'jira', title: 'Jira', icon: 'board', group: 'itsm', kind: 'card' },
  { key: 'microsoft', title: 'Microsoft 365', icon: 'mail', group: 'microsoft', kind: 'standalone' },
  { key: 'intune', title: 'Intune', icon: 'devices', group: 'microsoft', kind: 'standalone' },
  { key: 'teams-webhook', title: 'Teams Alerts', icon: 'shield', group: 'microsoft', kind: 'standalone' },
  { key: 'pagerduty', title: 'PagerDuty', icon: 'shield', group: 'incident-response', kind: 'standalone' },
  { key: 'incident-email', title: 'Incident Email', icon: 'mail', group: 'incident-response', kind: 'standalone' },
  { key: 's3', title: 'Amazon S3', icon: 'bucket', group: 'storage-dev', kind: 'card' },
  { key: 'confluence', title: 'Confluence', icon: 'book', group: 'storage-dev', kind: 'card' },
];

export default function Connectors() {
  const queryClient = useQueryClient();
  const [selectedKey, setSelectedKey] = useState(REGISTRY[0].key);
  const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, { ok: boolean | null; text: string }>>({});
  const [clearing, setClearing] = useState<Set<string>>(() => new Set());
  const [jiraOverrides, setJiraOverrides] = useState<JiraOverride[]>([]);
  const [cardVer, setCardVer] = useState<Record<string, number>>({});
  const [dirty, setDirty] = useState<Set<string>>(() => new Set());
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const [authKind, setAuthKind] = useState('');

  // /api/connectors (routes/connectors.js, KNOWN_CONNECTORS = keycloak/s3/jira/
  // outlook/teams/confluence) — NOT /api/settings/connectors, which is a
  // different router (routes/settings.js) with its own hardcoded route per key
  // (servicenow, intune, teams-webhook, ...) and none of these four. Query key
  // is distinct from 'settings-connectors' (used by the standalone cards above)
  // so the two never collide in the cache despite both being "connector" data.
  const { data: srv, isLoading: loading, error: loadError, refetch } = useQuery<Record<string, ConnectorData>>({
    queryKey: ['connectors'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/connectors');
      if (res.status === 401 || res.status === 403) {
        setAuthKind(res.status === 403 ? 'forbidden' : 'unauth');
        throw new Error('auth');
      }
      const json = await res.json();
      return json as Record<string, ConnectorData>;
    },
  });

  // Enabled-state for the list's status dots and the fleet count below. Same
  // query keys the standalone cards themselves use ('settings-connectors',
  // 'integrations-status'), so this piggybacks on their cache instead of
  // firing extra requests.
  const { data: standaloneData } = useConnectorsMap();
  const { data: msStatus } = useQuery<IntegrationsStatus>({
    queryKey: ['integrations-status'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/status');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  function isConnectorOn(key: string): boolean {
    if (key === 'microsoft') return !!msStatus?.microsoft;
    if (key === 'servicenow' || key === 'intune' || key === 'teams-webhook' || key === 'pagerduty' || key === 'incident-email') {
      return !!standaloneData?.[key]?.enabled;
    }
    // DB row key is underscored ('active_directory'); the nav key stays
    // hyphenated to match the URL path, same split as everywhere else this
    // card's key is used.
    if (key === 'active-directory') return !!standaloneData?.['active_directory']?.enabled;
    return !!enabledMap[key];
  }

  // Sync server data into local state
  useEffect(() => {
    if (!srv) return;
    const em: Record<string, boolean> = {};
    CARDS.forEach((c) => { em[c.key] = !!(srv[c.key] && srv[c.key].enabled); });
    setEnabledMap(em);
    const projMap = (srv.jira?.config?.projects) || {};
    setJiraOverrides(Object.keys(projMap).map((pid) => ({
      project: pid,
      projectKey: projMap[pid]?.projectKey || '',
    })));
    setCardVer((prev) => {
      const next = { ...prev };
      CARDS.forEach((c) => { if (!dirtyRef.current.has(c.key)) next[c.key] = (next[c.key] || 0) + 1; });
      return next;
    });
  }, [srv]);

  const saveMutation = useMutation({
    mutationFn: async ({ name, body }: { name: string; body: any }) => {
      const res = await pbAuthedFetch(`/api/connectors/${name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 401 || res.status === 403) throw new Error('auth');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connectors'] }),
  });

  const testMutation = useMutation({
    mutationFn: async ({ name, body }: { name: string; body: any }) => {
      const res = await pbAuthedFetch(`/api/connectors/${name}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 401 || res.status === 403) throw new Error('auth');
      return res.json();
    },
  });

  function toggleClear(rowId: string, on: boolean) {
    setClearing((prev) => {
      const n = new Set(prev);
      if (on) n.add(rowId); else n.delete(rowId);
      return n;
    });
  }

  function setResult(name: string, ok: boolean | null, text: string) {
    setResults((prev) => ({ ...prev, [name]: { ok, text } }));
  }

  function stgReadForm(card: CardDef) {
    const config: Record<string, any> = {};
    const secrets: Record<string, any> = {};
    let enabled = false;
    card.rows.forEach((r) => {
      const key = fieldKey(card.key, r.id);
      const el = document.getElementById(r.id) as HTMLInputElement | null;
      if (!el) return;
      if (key === 'enabled') { enabled = el.checked; return; }
      if (r.type === 'check') { config[key] = el.checked; }
      else if (r.type === 'password') {
        if (clearing.has(r.id)) secrets[key] = null;
        else if (el.value) secrets[key] = el.value;
      } else { config[key] = el.value.trim(); }
    });
    return { enabled, config, secrets };
  }

  function jiraOverridesMap() {
    const map: Record<string, { projectKey: string }> = {};
    jiraOverrides.forEach((row) => {
      const pid = (row.project || '').trim();
      const key = (row.projectKey || '').trim();
      if (pid && key) map[pid] = { projectKey: key };
    });
    return map;
  }

  function stgSave(card: CardDef) {
    const name = card.key;
    const body = stgReadForm(card);
    if (name === 'jira') {
      const projMap = jiraOverridesMap();
      if (Object.keys(projMap).length) body.config.projects = projMap;
    }
    setResult(name, null, 'Saving…');
    saveMutation.mutate({ name, body }, {
      onSuccess: (res) => {
        if (res && Array.isArray(res.secretsSet)) {
          setClearing((prev) => {
            const n = new Set(prev);
            card.rows.forEach((r) => n.delete(r.id));
            return n.size === prev.size ? prev : n;
          });
        }
        setResult(name, true, 'Saved ✓');
        setDirty((prev) => { const n = new Set(prev); n.delete(name); return n; });
      },
      onError: () => setResult(name, false, 'Save failed'),
    });
  }

  function stgTest(card: CardDef) {
    const name = card.key;
    const body = stgReadForm(card);
    setResult(name, null, 'Testing…');
    testMutation.mutate({ name, body: { config: body.config, secrets: body.secrets } }, {
      onSuccess: (res) => {
        const text = res.ok ? (`Connected ✓ ${res.detail || ''}`) : (`Failed: ${res.error || 'unknown'}`);
        setResult(name, !!res.ok, text);
      },
      onError: () => setResult(name, false, 'Test failed'),
    });
  }

  function markDirty(e: React.FormEvent) {
    const target = e.target as HTMLElement;
    const card = target.closest?.('[data-card]');
    const key = card?.getAttribute('data-card');
    if (!key) return;
    setDirty((prev) => { if (prev.has(key)) return prev; const n = new Set(prev); n.add(key); return n; });
  }

  const fleet = useMemo(() => {
    const cells = REGISTRY.map((r) => {
      const on = isConnectorOn(r.key);
      return {
        key: r.key,
        name: `${r.title} ${on ? 'enabled' : 'disabled'}`,
        value: 1,
        tone: on ? 'accent' : 'steel',
      };
    });
    const on = cells.filter((c) => c.tone === 'accent').length;
    return {
      cells, on,
      description: `Connector fleet: ${on} of ${REGISTRY.length} enabled.`,
    };
  }, [enabledMap, standaloneData, msStatus]);

  if (loading) {
    return (
      <div className="state">
        <div className="state__title">Loading connector settings…</div>
      </div>
    );
  }

  return (
    <>
      {authKind ? (
        <div className="stg-banner" role="alert">
          {authKind === 'forbidden'
            ? 'Your account is not an administrator. Connector settings are admin-only.'
            : <>You are not signed in. Sign in as an admin to manage connectors.</>}
        </div>
      ) : null}
      {loadError && !authKind ? (
        <div className="state state--risk">
          <div className="state__title">Could not load connector settings</div>
          <div className="state__body">
            <button className="btn btn--sm" onClick={() => refetch()}>Retry</button>
          </div>
        </div>
      ) : null}

      {!loadError ? (
        <SummaryBand eyebrow="Connector fleet" value={`${fleet.on} of ${REGISTRY.length} enabled`}>
          <Meter discrete label="Connector fleet" total={REGISTRY.length} segments={fleet.cells} description={fleet.description} />
          <Legend items={[
            { key: 'on', name: 'Enabled', value: fleet.on, tone: 'accent' },
            { key: 'off', name: 'Disabled', value: REGISTRY.length - fleet.on, tone: 'steel' },
          ]} />
        </SummaryBand>
      ) : null}

      {!loadError ? (
        <div className="stg-picker-shell">
          <nav className="stg-picker-list" aria-label="Connectors">
            {GROUPS.map((g) => {
              const items = REGISTRY.filter((r) => r.group === g.id);
              if (!items.length) return null;
              return (
                <div className="stg-picker-group" key={g.id}>
                  <h3 className="stg-picker-group-label">{g.label}</h3>
                  {items.map((r) => {
                    const on = isConnectorOn(r.key);
                    return (
                      <button
                        key={r.key}
                        type="button"
                        className={`stg-picker-item${selectedKey === r.key ? ' is-active' : ''}`}
                        aria-current={selectedKey === r.key ? 'true' : undefined}
                        onClick={() => setSelectedKey(r.key)}
                      >
                        <Icon name={r.icon} size={15} />
                        <span className="stg-picker-item-label">{r.title}</span>
                        <span className={`stg-picker-dot${on ? ' is-on' : ''}`} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </nav>

          <div className="stg-picker-detail">
            {selectedKey === 'microsoft' ? <MicrosoftConnectCard /> : null}
            {selectedKey === 'servicenow' ? <ServiceNowCard /> : null}
            {selectedKey === 'intune' ? <IntuneCard /> : null}
            {selectedKey === 'teams-webhook' ? <TeamsWebhookCard /> : null}
            {selectedKey === 'pagerduty' ? <PagerDutyCard /> : null}
            {selectedKey === 'incident-email' ? <IncidentEmailCard /> : null}
            {selectedKey === 'active-directory' ? <ActiveDirectoryCard /> : null}
            {(() => {
              const c = CARDS.find((card) => card.key === selectedKey);
              if (!c) return null;
              const cfg = (srv?.[c.key]?.config) || {};
              const on = !!enabledMap[c.key];
              const res = results[c.key];
              const isBusy = saveMutation.isPending || testMutation.isPending;
              return (
                <form
                  className="box"
                  data-card={c.key}
                  aria-labelledby={`stg_${c.key}_h`}
                  key={`${c.key}-${cardVer[c.key] || 0}`}
                  onSubmit={(e) => { e.preventDefault(); stgSave(c); }}
                  onChange={(e) => markDirty(e)}
                >
                  <header className="box__head">
                    <TitleIcon name={c.icon} />
                    <div className="box__head-text">
                      <h2 className="box__title" id={`stg_${c.key}_h`}>{c.title}</h2>
                    </div>
                    <div className="box__head-actions">
                      <span className={`pill${on ? ' pill--ok' : ''}`}><i />{on ? 'enabled' : 'disabled'}</span>
                    </div>
                  </header>
                  <div className="box__body">
                    <p className="stg-desc">{c.desc}</p>
                    {c.rows.map((r) => {
                      const key = fieldKey(c.key, r.id);
                      if (r.type === 'check') {
                        const isEnabled = key === 'enabled';
                        const checked = isEnabled ? on : !!cfg[key];
                        return (
                          <div className="stg-row" key={r.id}>
                            <label className="stg-check">
                              <input
                                type="checkbox"
                                id={r.id}
                                defaultChecked={checked}
                                onChange={isEnabled ? (e) => setEnabledMap((prev) => ({ ...prev, [c.key]: e.target.checked })) : undefined}
                              />{r.label}
                            </label>
                          </div>
                        );
                      }
                      if (r.type === 'password') {
                        // getConnectorPublic (routes/connectors.js) masks a set secret to
                        // this literal string in the config it returns — there's no
                        // separate secretsSet list, unlike the standalone cards' connector_config rows.
                        const isSet = cfg[key] === '••••••••';
                        const willClear = clearing.has(r.id);
                        const ph = isSet ? '•••••••• (set — leave blank to keep)' : 'not set';
                        const descId = `${r.id}-state`;
                        return (
                          <div className="stg-row" key={r.id}>
                            <label className="stg-label" id={`${r.id}-label`} htmlFor={r.id}>{r.label}</label>
                            <input className="stg-input" type="password" id={r.id} placeholder={ph} aria-describedby={descId} disabled={willClear} />
                            <span id={descId} className="pb-sr-only">
                              {willClear ? 'Queued for removal on save.' : isSet ? 'Currently set.' : 'Not set.'}
                            </span>
                            {isSet ? (
                              <label className="stg-check" style={{ marginTop: 'var(--stg-s2)', fontSize: 'var(--stg-fz-micro)', color: 'var(--ink-45)' }}>
                                <input
                                  type="checkbox"
                                  id={`${r.id}_clear`}
                                  checked={willClear}
                                  aria-labelledby={`${r.id}-label ${r.id}-clear-text`}
                                  onChange={(e) => toggleClear(r.id, e.target.checked)}
                                />
                                <span id={`${r.id}-clear-text`}>Clear this secret</span>
                              </label>
                            ) : null}
                          </div>
                        );
                      }
                      return (
                        <div className="stg-row" key={r.id}>
                          <label className="stg-label" htmlFor={r.id}>{r.label}</label>
                          <input className="stg-input" type={r.inputType || 'text'} id={r.id} placeholder={r.ph} defaultValue={cfg[key] != null ? String(cfg[key]) : ''} />
                        </div>
                      );
                    })}
                    {c.key === 'jira' && (
                      <div className="stg-row stg-subgroup" id="stg-jira-overrides">
                        <h3 className="stg-label">Per-project overrides</h3>
                        <div className="stg-hint">Issues for a mapped project file to its own Jira project key.</div>
                        {jiraOverrides.map((row, i) => (
                          <div key={i} className="stg-override-row">
                            <select
                              className="stg-input"
                              style={{ flex: 1 }}
                              aria-label={`Override ${i + 1} project`}
                              value={row.project}
                              onChange={(e) => { const v = e.target.value; setJiraOverrides((prev) => prev.map((r, j) => (j === i ? { ...r, project: v } : r))); }}
                            >
                              <option value="">— select project —</option>
                            </select>
                            <input
                              type="text"
                              className="stg-input"
                              style={{ flex: 1 }}
                              placeholder="Jira project key"
                              aria-label={`Override ${i + 1} key`}
                              value={row.projectKey}
                              onChange={(e) => { const v = e.target.value; setJiraOverrides((prev) => prev.map((r, j) => (j === i ? { ...r, projectKey: v } : r))); }}
                            />
                            <button type="button" className="btn btn--sm" onClick={() => setJiraOverrides((prev) => prev.filter((_, j) => j !== i))}>Remove</button>
                          </div>
                        ))}
                        <button type="button" className="btn btn--sm" onClick={() => setJiraOverrides((prev) => [...prev, { project: '', projectKey: '' }])}>+ Add override</button>
                      </div>
                    )}
                  </div>
                  <div className="box__foot" style={{ display: 'flex', alignItems: 'center', gap: 'var(--stg-s3)', flexWrap: 'wrap' }}>
                    <button type="submit" className="btn btn--primary btn--sm" disabled={isBusy}>Save</button>
                    <button type="button" className="btn btn--sm" disabled={isBusy} onClick={() => stgTest(c)}>Test connection</button>
                    {dirty.has(c.key) ? <span className="stg-note">Unsaved changes</span> : null}
                    {res ? <span className={`stg-fig ${res.ok === true ? 't-ok' : res.ok === false ? 't-risk' : ''}`} style={{ marginLeft: 'auto', fontWeight: 600 }}>{res.text}</span> : null}
                  </div>
                </form>
              );
            })()}
          </div>
        </div>
      ) : null}
    </>
  );
}
